import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { userApi, auditApi } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";

const UserVisibilityTab = () => {
  const [users, setUsers] = useState([]);
  const [viewerUserId, setViewerUserId] = useState("");
  const [targetUserIds, setTargetUserIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const { user, tenant_id, is_admin } = useContext(AuthContext);

  useEffect(() => {
    if (!user || !tenant_id) return;

    // Check if user is admin
    if (!is_admin) {
      setMessage("❌ You must be an admin to assign visibility.");
      return;
    }

    fetchUsers();
  }, [user, tenant_id, is_admin]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      // Use the userApi from api.js which uses secureAxios
      const response = await userApi.getAll();
      const sorted = response.data.sort((a, b) => a.name.localeCompare(b.name));
      setUsers(sorted);
    } catch (err) {
      console.error("Error fetching users", err);
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  // Fetch current visibility settings when viewer is selected
  useEffect(() => {
    if (!viewerUserId) {
      setTargetUserIds([]);
      return;
    }

    fetchCurrentVisibility();
  }, [viewerUserId]);

  const fetchCurrentVisibility = async () => {
    try {
      setLoading(true);
      // Get current visibility links for the selected viewer
      const response = await secureAxios.get(
        `/api/users/visibility-links/${viewerUserId}`
      );
      const currentTargets = response.data.map((link) => link.target_user_id);
      setTargetUserIds(currentTargets);
    } catch (err) {
      console.error("Error fetching current visibility", err);
      // It's okay if this fails - user might not have any visibility set yet
      setTargetUserIds([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (targetId) => {
    setTargetUserIds((prev) =>
      prev.includes(targetId)
        ? prev.filter((id) => id !== targetId)
        : [...prev, targetId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!viewerUserId) {
      toast.error("Please select a viewer user");
      return;
    }

    try {
      setLoading(true);
      setMessage("");

      // Get viewer user details for audit log
      const viewerUser = users.find((u) => u.user_id === viewerUserId);

      // Use secureAxios for HIPAA-compliant request
      await secureAxios.post("/api/users/assign-visibility", {
        viewer_user_id: viewerUserId,
        target_user_ids: targetUserIds,
      });

      // Log audit event for visibility assignment
      try {
        await auditApi.logEntry({
          action: "ASSIGN_USER_VISIBILITY",
          entity_type: "USER_VISIBILITY",
          entity_id: viewerUserId,
          entity_identifier: viewerUser?.email || viewerUserId,
          action_status: "SUCCESS",
          contains_phi: true,
          purpose_of_use: "OPERATIONS",
          details: {
            viewer_user_id: viewerUserId,
            viewer_name: viewerUser?.name,
            viewer_email: viewerUser?.email,
            target_user_count: targetUserIds.length,
            target_user_ids: targetUserIds,
            assigned_by: user.email,
            reason: "Admin assigned user visibility permissions",
          },
        });
      } catch (auditError) {
        console.error("Failed to log audit event:", auditError);
        // Don't fail the operation if audit logging fails
      }

      setMessage("✅ Visibility successfully assigned!");
      toast.success("Visibility settings updated successfully");

      // Refresh the current visibility to confirm changes
      fetchCurrentVisibility();
    } catch (err) {
      console.error("Error assigning visibility", err);

      const errorMessage =
        err.response?.data?.error || "Failed to assign visibility";
      setMessage(`❌ Error: ${errorMessage}`);
      toast.error(errorMessage);

      // Log failed attempt
      try {
        await auditApi.logEntry({
          action: "ASSIGN_USER_VISIBILITY",
          entity_type: "USER_VISIBILITY",
          entity_id: viewerUserId,
          action_status: "FAILED",
          contains_phi: true,
          purpose_of_use: "OPERATIONS",
          details: {
            viewer_user_id: viewerUserId,
            error: errorMessage,
            assigned_by: user.email,
          },
        });
      } catch (auditError) {
        console.error("Failed to log audit error:", auditError);
      }
    } finally {
      setLoading(false);
    }
  };

  // If not admin, show error message
  if (!is_admin) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Access Denied</h4>
          <p>
            You must be an administrator to access user visibility settings.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <h3 className="mb-4">Assign User Visibility</h3>

      {loading && (
        <div className="text-center mb-3">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="form-label">
            <strong>Viewer User</strong>
          </label>
          <select
            className="form-select"
            value={viewerUserId}
            onChange={(e) => setViewerUserId(e.target.value)}
            required
            disabled={loading}
          >
            <option value="">-- Select Viewer --</option>
            {users.map((u) => (
              <option key={u.user_id} value={u.user_id}>
                {u.name} ({u.email}) - {u.role || "No Role"}
              </option>
            ))}
          </select>
          <div className="form-text">
            Select the user who will be granted visibility to view other users
          </div>
        </div>

        <div className="mb-4">
          <label className="form-label">
            <strong>Users they can view</strong>
          </label>
          {viewerUserId && (
            <div className="form-text mb-2">
              Currently selected: {targetUserIds.length} user(s)
            </div>
          )}
          <div
            className="border rounded p-3"
            style={{ maxHeight: "300px", overflowY: "auto" }}
          >
            {users.map((u) => (
              <div className="form-check" key={u.user_id}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`user-${u.user_id}`}
                  value={u.user_id}
                  checked={targetUserIds.includes(u.user_id)}
                  onChange={() => handleCheckboxChange(u.user_id)}
                  disabled={u.user_id === viewerUserId || loading}
                />
                <label
                  className="form-check-label"
                  htmlFor={`user-${u.user_id}`}
                  style={{
                    opacity: u.user_id === viewerUserId ? 0.5 : 1,
                    fontStyle: u.user_id === viewerUserId ? "italic" : "normal",
                  }}
                >
                  {u.name} ({u.email}) - {u.role || "No Role"}
                  {u.user_id === viewerUserId && " (self - disabled)"}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="d-flex gap-2">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !viewerUserId}
          >
            {loading ? "Saving..." : "Save Visibility"}
          </button>

          {viewerUserId && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setTargetUserIds([]);
                setMessage("");
              }}
              disabled={loading}
            >
              Clear All
            </button>
          )}
        </div>

        {message && (
          <div
            className={`mt-3 alert ${
              message.includes("✅") ? "alert-success" : "alert-danger"
            }`}
          >
            {message}
          </div>
        )}
      </form>

      <div className="mt-4 p-3 bg-light rounded">
        <h5>About User Visibility</h5>
        <p className="mb-0">
          User visibility controls which users can view other users' data in the
          system. This is particularly important for job coaches who need to see
          specific consumers they're assigned to work with.
        </p>
      </div>
    </div>
  );
};

export default UserVisibilityTab;
